import { useState, useEffect, useCallback } from 'react';
import { getStorageItem, setStorageItem, removeStorageItem } from '@/utils/storage/local-storage';

// timezone is not persisted on the session — it's display-only context for interpreting UTC timestamps
// this hook keeps the selected timezone in localStorage so it survives a refresh, scoped by project UUID
// so navigating to a different project always starts clean

const TIMEZONE_KEY = 'last-timezone';
const TIMEZONE_PROJECT_KEY = 'last-timezone-project';
const TTL_HOURS = 24 * 30;

const readStoredTimezone = (projectUuid: string | undefined): string | undefined => {
  if (!projectUuid) return undefined;

  try {
    const storedProjectUuid = getStorageItem(TIMEZONE_PROJECT_KEY);

    if (storedProjectUuid === projectUuid) {
      return getStorageItem(TIMEZONE_KEY) ?? undefined;
    }
  } catch (error) {
    console.error('Failed to read timezone from storage:', error);
  }

  return undefined;
};

const usePersistedTimezone = (projectUuid: string | undefined): [string | undefined, (tz: string) => void] => {
  const [timezone, setTimezoneState] = useState<string | undefined>(() => readStoredTimezone(projectUuid));

  useEffect(() => {
    if (!projectUuid) return;

    try {
      if (getStorageItem(TIMEZONE_PROJECT_KEY) === projectUuid) {
        setTimezoneState(getStorageItem(TIMEZONE_KEY) || undefined);
      } else {
        removeStorageItem(TIMEZONE_KEY);
        setStorageItem(TIMEZONE_PROJECT_KEY, projectUuid, TTL_HOURS);
        setTimezoneState(undefined);
      }
    } catch {
      // stale plain-string values from before setStorageItem migration — clear and start fresh
      removeStorageItem(TIMEZONE_KEY);
      removeStorageItem(TIMEZONE_PROJECT_KEY);
      setStorageItem(TIMEZONE_PROJECT_KEY, projectUuid, TTL_HOURS);
      setTimezoneState(undefined);
    }
  }, [projectUuid]);

  const setTimezone = useCallback(
    (tz: string) => {
      setTimezoneState(tz);
      setStorageItem(TIMEZONE_KEY, tz, TTL_HOURS);
      if (projectUuid) {
        setStorageItem(TIMEZONE_PROJECT_KEY, projectUuid, TTL_HOURS);
      }
    },
    [projectUuid]
  );

  return [timezone, setTimezone];
};

export default usePersistedTimezone;
